// src/components/hrv_dash.js
import React, { useState, useEffect, useRef } from 'react';
import Plot from "react-plotly.js";
import useClientStore from '../stores/client';
import Pako from 'pako';

const HrvDash = (props) => {
  const offset = 1100;
  const WINDOW_SEC = 12; // 보여줄 구간
  const { client, addOnMessage, removeOnMessage } = useClientStore();

  // HRV/ECG 토픽 & 최신 ECG 버퍼
  const hrvTopicRef = useRef('');
  const ecgTopicRef = useRef('');
  const dataTopicRef = useRef('');
  const latestEcgRef = useRef({ fs: 125, buf: [] });
  const calibRef = useRef({ a: 100, b: -60 }); // 초기(임의): STRESS ≈ 100 + (-60)*log10(rmssd)
  const pairsRef = useRef([]);                 // 최근 ECG (rmssd, stress) 쌍
  const MAX_CALIB_POINTS = 90;                 // 최근 90초 정도 누적

  function fitLogLinear(pairs) {
  // x = log10(rmssd), y = stress
  const xs = [], ys = [];
  for (const p of pairs) {
    if (!p || p.rmssd <= 0 || p.stress == null) continue;
    xs.push(Math.log10(p.rmssd));
    ys.push(p.stress);
  }
  const n = xs.length;
  if (n < 10) return null; // 데이터 적으면 보정하지 않음

  let sx=0, sy=0, sxx=0, sxy=0;
  for (let i=0;i<n;i++){ sx+=xs[i]; sy+=ys[i]; sxx+=xs[i]*xs[i]; sxy+=xs[i]*ys[i]; }
  const denom = n*sxx - sx*sx;
  if (Math.abs(denom) < 1e-6) return null;

  const b = (n*sxy - sx*sy) / denom;
  const a = (sy - b*sx) / n;
  return { a, b };
}

function ecgLikeStressFromRmssd(rmssd) {
  if (!rmssd || rmssd <= 0) return null;
  const { a, b } = calibRef.current;
  const y = a + b * Math.log10(rmssd);
  return Math.max(0, Math.min(100, y));
}

function meanStd(arr) {
  const xs = arr.filter(v => v != null && !isNaN(v));
  if (xs.length === 0) return { mean: null, std: null };
  const m = xs.reduce((a,b)=>a+b,0) / xs.length;
  const v = xs.reduce((a,b)=>a+(b-m)*(b-m),0) / Math.max(1, xs.length-1);
  const s = Math.sqrt(v);
  return { mean: m, std: s > 1e-9 ? s : 1e-9 };
}
function z(v, m, s) { return (v - m) / s; }
function dist3(ax, ay, az, bx, by, bz) {
  const dx = ax-bx, dy = ay-by, dz = az-bz;
  return Math.sqrt(dx*dx + dy*dy + dz*dz);
}
  

  const [data, setData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [graphLayout, setGraphLayout] = useState({
    autosize: true,
    xaxis: {
      showgrid: true, zeroline: false, showticklabels: false,
      dtick: 0.25, gridcolor: 'rgba(221,221,221,0.2)',
      range: [-1.7, 12.3]
    },
    yaxis: {
      showgrid: true, zeroline: false, showticklabels: false,
      gridcolor: 'rgba(221,221,221,0.2)',
      dtick: offset / 4,
      range: [-1500, 5 * 3000]
    },
    margin: { l: 0, r: 0, t: 0, b: 0 },
    showlegend: false,
    // rgba(34, 54, 91,1)
    plot_bgcolor: '#000000', // Set the plot background color to black
    paper_bgcolor: 'white',  // Set the paper background color to black
    font: { color: 'red' },  // Set font color to white for better contrast
    annotations: []
  });

    // 좌상단 ECG 오버레이( pub_device_data 기준 )
  const [ecgOverlay, setEcgOverlay] = useState({
    hr: 0, stress:null, rmssd: null, sdnn: null, pnn50: null, ready: false, rr_count: null,
  });

  const [bcgOverlay, setBcgOverlay] = useState({
    hr: 0, stress: null, rmssd: null, sdnn: null, pnn50: null, ready: false, index: null,
  });

  // props.data -> state
  useEffect(() => { setData(props.data); }, [props.data]);

  // HRV + ECG 구독
  useEffect(() => {
    if (!data || !client) return;

    const hrvTopic = `aw/${data.device_sn}/stream/hrv`;
    const ecgTopic = `aw/Solmi001/stream/ecg`; // 필요시 고정/동적 변경
    const dataTopic = `SmartMat/Solmi001/Autowells-server/pub_device_data`;

    hrvTopicRef.current = hrvTopic;
    ecgTopicRef.current = ecgTopic;
    dataTopicRef.current = dataTopic;

    client.subscribe(hrvTopic, (err) => err ? console.log('Subscribe HRV error', err) : console.log('✅', hrvTopic));
    client.subscribe(ecgTopic, (err) => err ? console.log('Subscribe ECG error', err) : console.log('✅', ecgTopic));
    client.subscribe(dataTopic, (err) => err ? console.log('Subscribe DATA error', err) : console.log('✅', dataTopic));

    addOnMessage(hrvTopic, onMessage);
    addOnMessage(ecgTopic, onMessage);
    addOnMessage(dataTopic, onMessage);

    return () => {

    };
  }, [data, client]);

    // ───────── 유틸(압축 해제) ─────────
  const inflateBase64 = (msg) => {
    try { return Pako.inflate(Uint8Array.from(atob(msg), c => c.charCodeAt(0)), { to: 'string' }); }
    catch { return null; }
  };
  const inflateRaw = (msg) => { try { return Pako.inflate(msg, { to: 'string' }); } catch { return null; } };

  const parseIncomingMessage = (message) => {
  let text = '';

  try {
    // (1) 메시지가 문자열이면: JSON or base64 압축 데이터
    if (typeof message === 'string') {
      try {
        // base64 디코딩 시도
        const binary = Uint8Array.from(atob(message), c => c.charCodeAt(0));
        if (binary[0] === 0x1f && binary[1] === 0x8b) {
          text = Pako.inflate(binary, { to: 'string' });  // gzip 압축일 경우만
        } else {
          text = new TextDecoder().decode(binary);        // 압축 아님
        }
      } catch {
        text = message;  // 그냥 일반 문자열
      }
    }

    // (2) 메시지가 Uint8Array 또는 ArrayBuffer일 경우
    else if (message instanceof Uint8Array || message instanceof ArrayBuffer) {
      const u8 = message instanceof Uint8Array ? message : new Uint8Array(message);
      if (u8[0] === 0x1f && u8[1] === 0x8b) {
        text = Pako.inflate(u8, { to: 'string' });  // 압축된 경우만
      } else {
        text = new TextDecoder().decode(u8);        // 압축 안 됨
      }
    }

    // (3) Buffer 타입인 경우도 대응 (Node 환경 대비)
    else if (typeof Buffer !== "undefined" && Buffer.isBuffer(message)) {
      const u8 = Uint8Array.from(message);
      if (u8[0] === 0x1f && u8[1] === 0x8b) {
        text = Pako.inflate(u8, { to: 'string' });
      } else {
        text = new TextDecoder().decode(u8);
      }
    }

    // (4) 실패 시 fallback
    else {
      console.warn('Unsupported message format:', message);
      return null;
    }
  } catch (err) {
    console.warn('❌ Failed to decompress message:', err);
    return null;
  }

  // 최종 JSON 파싱
  try {
    return JSON.parse(text);
  } catch (err) {
    console.warn('❌ Failed to parse JSON:', err, text);
    return null;
  }
};


  const handlePubDeviceData = (obj) => {
    const arr = Array.isArray(obj?.e) ? obj.e : null;
    if (!arr || !arr.length) return;


    const last = arr[arr.length - 1];

    const hr = Number(last?.hr) || 0;
    const stress = (last?.stress != null) ? Number(last.stress) : null;
    const rmssd = (last?.rmssd_ms != null) ? Number(last.rmssd_ms) : null;
    const sdnn  = (last?.sdnn_ms  != null) ? Number(last.sdnn_ms)  : null;
    const pnn50 = (last?.pnn50    != null) ? Number(last.pnn50)    : null;
    const ready = rmssd != null && sdnn != null && pnn50 != null;

    setEcgOverlay({ hr,stress, rmssd, sdnn, pnn50, ready });

    if (ready && rmssd > 0 && stress != null) {
      pairsRef.current.push({ rmssd, stress });
      if (pairsRef.current.length > MAX_CALIB_POINTS) pairsRef.current.shift();

      const fit = fitLogLinear(pairsRef.current);
      if (fit) calibRef.current = fit; // 보정 계수 갱신
    }
  };

  const calcBcgStress = (rmssd) => {
    if (!rmssd || rmssd <= 0) return null;
    // RMSSD 10ms → stress 80~90
    // RMSSD 50ms → stress 20~30
    // ECG scale에 맞추기 위해 log 또는 지수 스케일 사용
    const capped = Math.max(5, Math.min(rmssd, 80)); // 5~80ms 사이로 clip
    const stress = 100 - (Math.log10(capped) / Math.log10(80)) * 100;
    return Math.min(Math.max(stress, 0), 100);
  };

  


  // HRV/ECG 공용 수신
  const onMessage = (topic, message) => {
    if (topic !== hrvTopicRef.current && topic !== ecgTopicRef.current && topic !== dataTopicRef.current) return;

    if (topic === dataTopicRef.current) {
      const parsed = parseIncomingMessage(message);
      handlePubDeviceData(parsed);     // ★ ECG 수치 업데이트(좌상단 오버레이)
      return;
    }

    // Base64(+gzip)/gzip/문자열 모두 처리
    let decompressMessage = null;
    try {
      const compressed = Uint8Array.from(atob(message), c => c.charCodeAt(0));
      decompressMessage = Pako.inflate(compressed, { to: 'string' });
    } catch {
      try { decompressMessage = Pako.inflate(message, { to: 'string' }); }
      catch { decompressMessage = (typeof message === 'string') ? message : ''; }
    }
    if (!decompressMessage) return;



    let parsed; try { parsed = JSON.parse(decompressMessage); } catch { return; }
    const payload = parsed?.timestamp ? parsed.data : parsed;
    if (!payload) return;


    

    // ECG 토픽: 최신 샘플 버퍼에 저장만
    if (topic === ecgTopicRef.current) {
      let samples = null;
      let fs = latestEcgRef.current.fs || 125;
      if (Array.isArray(payload?.samples)) {
        samples = payload.samples;
        fs = Number(payload.fs || payload.sampling_rate || fs);
      } else if (Array.isArray(payload?.ecg_result?.[0])) {
        samples = payload.ecg_result[0];
        fs = Number(payload.sampling_rate || fs);
      }
      if (samples?.length) {
        const buf = latestEcgRef.current.buf;
        for (let i = 0; i < samples.length; i++) buf.push(samples[i]);
        latestEcgRef.current.fs = fs;
        const maxN = Math.floor(WINDOW_SEC * fs);
        if (buf.length > maxN) buf.splice(0, buf.length - maxN); // 최근 WINDOW_SEC초만 유지
      }
      return;
    }

    // HRV 토픽: 최신 ECG를 주입해서 그림
    if (topic === hrvTopicRef.current) {
      const merged = { ...payload };
      const ecgFs  = latestEcgRef.current.fs || 125;
      const ecgBuf = latestEcgRef.current.buf;
      if (ecgBuf?.length) {
        merged.ecg_result = [ ecgBuf.slice() ]; // 복사본
        merged.ecg_fs = ecgFs;
        merged.sampling_rate = Number(merged.sampling_rate || ecgFs || 125);
      }
      procData(merged);
    }
  };

  // === 실제 그리기 (디자인/폰트/주석 유지) ===
  const procData = (recieveData) => {
    if (!recieveData) return;

    // 준비
    let traces = [];
    let peaksTraces = [];
    const annotations = [];
    let bestIndex = null;
    let bestDist  = Infinity;
    let bestDiff = Infinity;
    
    if (Array.isArray(recieveData.bcg_results) && recieveData.bcg_results.length > 0) {
      // 짝수 인덱스(0,2,4,...)만 BCG "심장" 라인
      const evenIdx = recieveData.bcg_results
        .map((_, i) => i)
        .filter(i => i % 2 === 0);

      // 각 채널의 HRV 값 모으기
      const rmssds = [];
      const sdnns  = [];
      const pnn50s = [];
      const mapH = new Map(); // i -> {rmssd, sdnn, pnn50, hr}

      evenIdx.forEach(i => {
        const h = recieveData.bcg_results[i]?.hrv_analysis_data || {};
        const rmssd = (h.rmssd != null) ? Number(h.rmssd) : null;
        const sdnn  = (h.sdnn  != null) ? Number(h.sdnn)  : null;
        const pnn50 = (h.pnn50 != null) ? Number(h.pnn50) : null;
        if (rmssd != null && sdnn != null && pnn50 != null) {
          rmssds.push(rmssd); sdnns.push(sdnn); pnn50s.push(pnn50);
          mapH.set(i, { rmssd, sdnn, pnn50, hr: h.hr != null ? Number(h.hr) : null });
        }
      });

      // 유효 채널 없으면 스킵
      if (mapH.size > 0) {
        // BCG 전체 분포로 표준화 파라미터
        const { mean: mR, std: sR } = meanStd(rmssds);
        const { mean: mS, std: sS } = meanStd(sdnns);
        const { mean: mP, std: sP } = meanStd(pnn50s);

        // ECG 기준(좌상단)도 같은 축으로 표준화
        const targetRmssd = ecgOverlay?.rmssd != null ? Number(ecgOverlay.rmssd) : null;
        const targetSdnn  = ecgOverlay?.sdnn  != null ? Number(ecgOverlay.sdnn)  : null;
        const targetPnn50 = ecgOverlay?.pnn50 != null ? Number(ecgOverlay.pnn50) : null;

        // ECG 지표가 없으면, BCG 평균(=0 z-score)으로 타깃 설정
        const tZR = (targetRmssd != null) ? z(targetRmssd, mR, sR) : 0;
        const tZS = (targetSdnn  != null) ? z(targetSdnn , mS, sS) : 0;
        const tZP = (targetPnn50 != null) ? z(targetPnn50, mP, sP) : 0;

        // 각 채널과 타깃(ECG) 사이의 z-공간 거리
        for (const [i, v] of mapH.entries()) {
          const zr = z(v.rmssd, mR, sR);
          const zs = z(v.sdnn , mS, sS);
          const zp = z(v.pnn50, mP, sP);
          const d = dist3(zr, zs, zp, tZR, tZS, tZP);
          if (d < bestDist) { bestDist = d; bestIndex = i; }
        }
      }
    }

    // BEST가 정해졌다면 BCG 오버레이 갱신 (+ ECG 스케일로 보정된 stress)
    if (bestIndex !== null) {
      const best = recieveData.bcg_results[bestIndex]?.hrv_analysis_data || {};
      const rmssd_bcg = best.rmssd != null ? Number(best.rmssd) : null;
      const stress_bcg = ecgLikeStressFromRmssd(rmssd_bcg);

      setBcgOverlay({
        hr: Math.round(best.hr || 0),
        stress: stress_bcg,                 // ECG 스케일에 정렬된 STRESS
        rmssd: rmssd_bcg,
        sdnn: best.sdnn != null ? Number(best.sdnn) : null,
        pnn50: best.pnn50 != null ? Number(best.pnn50) : null,
        ready: best.rmssd != null && best.sdnn != null && best.pnn50 != null,
        index: bestIndex
      });
    }

    // 샘플레이트
    const sampling_rate = Number(recieveData.sampling_rate || 100);

    // 시간 범위 (BCG 길이가 없으면 WINDOW_SEC)
    let ts_range_max = WINDOW_SEC;
    if (Array.isArray(recieveData?.bcg_results) && recieveData.bcg_results[0]?.filtered?.length) {
      ts_range_max = recieveData.bcg_results[0].filtered.length / sampling_rate;
    }
    let ts_range_min = 0;
    if (ts_range_max === 22) ts_range_min = 12;
    else if (ts_range_max === 12) ts_range_min = 2;

    let fsr_sum = recieveData.fsr_sum;
    let resp = recieveData.resp_rate;
    let hr = recieveData.heart_rate;
    let motion_detect = recieveData.motion_detected;
    let apnea_detect = recieveData.apnea_detected;

    // 화면별 폰트/배치
    const screenWidth = window.innerWidth;
    let titleFontSize = 24, subtitleFontSize = 20, labelFontSize = 24, annotationFontSize = 12, annotationOffset = 600;
    let font_size="font-size:75px;", font_size2="font-size:90px;", font_size3="font-size:20px;";
    const baseLayout = { ...graphLayout };
    baseLayout.xaxis = { ...baseLayout.xaxis, range: [-1.7, 12.3] };

    if (screenWidth > 1680) {
      // as-is
    } else if (screenWidth > 1500) {
      titleFontSize = 26; subtitleFontSize = 16; labelFontSize = 17; annotationFontSize = 9; annotationOffset = 300;
      font_size="font-size:65px;"; font_size2="font-size:85px;";
    } else if (screenWidth > 1000) {
      titleFontSize = 24; subtitleFontSize = 14; labelFontSize = 18; annotationFontSize = 10; annotationOffset = 300;
      font_size="font-size:55px;"; font_size2="font-size:75px;"; font_size3="font-size:18px;";
    } else {
      if (screenWidth <= 1000) {
        font_size="display:none;"; font_size2="display:none;"; font_size3="display:none;";
        baseLayout.xaxis.range = [-1.8, 10.5];
      }
      titleFontSize = 22; subtitleFontSize = 12; labelFontSize = 16; annotationFontSize = 8; annotationOffset = 300;
    }

    // ===== BCG 배치: 1~4만 보이도록 가시 인덱스 계산 =====
let visCount = 0;
let pairVisiblePos = new Map();
if (recieveData.bcg_results) {
  const totalPairs = Math.floor(recieveData.bcg_results.length / 2);
  const visiblePairs = Array.from({ length: totalPairs }, (_, k) => k).filter(k => k < 4); // BCG1~4만
  visiblePairs.forEach((p, idx) => pairVisiblePos.set(p, idx)); // ex) pair 0→0, 1→1, 2→2, 3→3
  visCount = visiblePairs.length; // 4
}
// 💡 표시용 y오프셋 헬퍼(‘보이는 줄’만 기준)
const yBaseOf = (i) => {
  const pairIdx = Math.floor(i / 2);              // 0=BCG1, 1=BCG2, ...
  const visPos  = pairVisiblePos.get(pairIdx);    // 0..3
  if (visPos === undefined) return null;          // 숨김(pair 5/6)
  return (visCount - visPos - 1) * offset;        // 위로 갈수록 커짐
};

// y축 범위도 ‘보이는 줄’ 수에 맞춰 재계산 (상단 여유 800 유지)
baseLayout.yaxis = { ...baseLayout.yaxis, range: [-1500, visCount * offset + 800] };

    // ===== ECG: 각 BCG 라인 위에 노란색으로 겹쳐서 표시 =====
    if (recieveData.ecg_result) {
      const ecgFs = Number(recieveData.ecg_fs || recieveData.ecg_sampling_rate || 125);
      // 1채널만 온다고 가정(배열 여러개여도 첫 채널 사용)
      const ecgArr = Array.isArray(recieveData.ecg_result[0]) ? recieveData.ecg_result[0] : null;

      if (ecgArr?.length && visCount > 0) {
        const ts = Array.from({ length: ecgArr.length }, (_, k) => k / ecgFs);
        const from = Math.max(0, ts.findIndex(t => t >= ts_range_min));
        let to = ts.findIndex(t => t > ts_range_max);
        if (to === -1) to = ecgArr.length;

        const ecg_ts = ts.slice(from, to).map(v => v - ts_range_min);
        const ecg_sig = ecgArr.slice(from, to).map(v => v * 2.5); // 기존 배율 유지

        // 각 가시 BCG 페어(yBase)에 동일 ECG를 겹쳐 그림
        pairVisiblePos.forEach((visPos, pairIdx) => {
            // i=pairIdx*2 (심장 라인)에 맞춰 yBase 계산
            const iForThisPair = pairIdx * 2;
            const yBase = yBaseOf(iForThisPair);                   // ✅ 여기!
            traces.push({
                x: ecg_ts,
                y: ecg_sig.map(v => v + yBase),                      // ✅ 여기!
                mode: 'lines',
                line: { color: 'yellow', width: 2 },
                type: 'scattergl'
            });
        });
      }
      // ECG 라벨은 따로 표시하지 않음(요청: 겹치기만)
    } else {
      baseLayout.yaxis = { ...baseLayout.yaxis, range: [-1500, 6 * 2800] };
    }

    // ===== BCG 신호/피크/좌측 주석 (BCG1~4만) =====
    if (recieveData.bcg_results) {
      const respScaleFactor = 4;
      const heartColors = ['#DAA520', "rgb(81, 172, 201)", '#4682B4', '#32CD32', '#8A2BE2', '#2E8B57'];
      let min_difference = Infinity;

      recieveData.bcg_results.forEach((bcgResult, i) => {
        const yBase = yBaseOf(i);
        if (yBase === null) return; // BCG5,6은 스킵

        const ts = Array.from({ length: bcgResult.filtered.length }, (_, j) => j / sampling_rate);
        let filteredSignal = bcgResult.filtered;

        const filtered_ts = ts.filter(t => t >= ts_range_min && t <= ts_range_max).map(t => t - ts_range_min);
        let filtered_signal = filteredSignal.slice(ts.findIndex(t => t >= ts_range_min), ts.findIndex(t => t > ts_range_max));

        // HR 매칭 로직 유지
        if (bcgResult.hrv_analysis_data){
          const resultHeartRate = bcgResult.hrv_analysis_data.hr? bcgResult.hrv_analysis_data.hr.toFixed(2) : 0.0;
          if (resultHeartRate !== 0.0){
            let heart_rate = recieveData.heart_rate ? Math.round(recieveData.heart_rate).toLocaleString('en-US', { minimumIntegerDigits: 3, useGrouping: false }) : 0.0;
            const difference = Math.abs(resultHeartRate - heart_rate);
            if (difference < min_difference && bcgResult.jpeaks.length > 3){
              min_difference = difference;
            }
          }
        }

        let color;
          if (i % 2 === 0) {
    const jpeaks = bcgResult.jpeaks
      .filter(idx => ts[idx] >= ts_range_min && ts[idx] <= ts_range_max)
      .map(idx => idx - ts.findIndex(t => t >= ts_range_min));

    peaksTraces.push({
      x: filtered_ts.filter((_, idx) => jpeaks.includes(idx)),
      y: jpeaks.map(idx => filtered_signal[idx] + yBase),   // ✅ 여기!
      mode: 'markers+text',
      text: jpeaks.map((_, idx) => `J${idx}`),
      textposition: 'top center',
      type: 'scattergl',
      marker: { color: "red" }
    });
  } else {
    filtered_signal = filtered_signal.map(val => val * 4);
  }

  traces.push({
    x: filtered_ts,
    y: filtered_signal.map(val => val + yBase),              // ✅ 여기!
    mode: 'lines',
    line: { color: (i % 2 === 0 ? '#00fc00' : '#00fcff'), width: 2 },
    type: 'scattergl'
  });

        if (i % 2 === 0) {
          const h = bcgResult.hrv_analysis_data || {};
          const rmssd = h.rmssd ? h.rmssd.toFixed(2) : 'N/A';
          const sdnn  = h.sdnn  ? h.sdnn.toFixed(2)  : 'N/A';
          const pnn50 = h.pnn50 ? h.pnn50.toFixed(2) : 'N/A';
          const st_lv = h.st_lv ? h.st_lv.toFixed(3) : 'N/A';
          const heartIndex = Math.floor(i / 2) % heartColors.length;
          const signal_color = heartColors[heartIndex];

          annotations.push({
            x: 0.01, // 오른쪽 끝 위치
            y: yBase,    
            xref: 'paper',
            yref: 'y',
            text:
              '<span style="color:' + signal_color + `;font-size:${labelFontSize}px;"><b>BCG ` + (Math.floor(i / 2) + 1) + '</b></span><br><br />' +
              '<span style="display:block;text-align:left;color:#fff;">  ST_LV :        ' + st_lv + '    </span><br>' +
              '<span style="display:block;text-align:left;color:#fff;"> RMSSD :       ' + rmssd + ' ms</span><br>' +
              '<span style="display:block;text-align:left;color:#fff;">    SDNN :       ' + sdnn + ' ms</span><br>' +
              '<span style="display:block;text-align:left;color:#fff;">  pNN50 :       ' + pnn50 + ' % </span>',
            showarrow: false,
            font: { size: annotationFontSize, family: 'Arial', color: 'black' },
            xanchor: 'left',
          });
        }
      });
    }

    // ===== 우측 요약 박스(기존 유지) =====
    if(fsr_sum<300 || motion_detect===1){
      hr=0; resp=0;
      resp=String(resp).padStart(2,0);
      if(motion_detect===1){
        font_size="font-size:60px;"; fsr_sum="Moving";
      }else{
        fsr_sum=String(fsr_sum.toFixed(0)).padStart(4,0);
      }
    }else{
      hr=hr.toFixed(0);
      if(apnea_detect){ font_size2="font-size:60px;"; resp="Apnea"; }
      else { resp=String(resp.toFixed(0)).padStart(2,0); }
      fsr_sum=String(fsr_sum.toFixed(0)).padStart(4,0);
    }

    if (screenWidth > 1000) {
      annotations.push({
        xref: 'paper', yref: 'paper',
        x: 0.835, xanchor: 'left', y: 0.88, yanchor: 'top',
        text:
          '<span style="color:#00fc00;'+font_size2+'text-align:center;"><b>'+String(hr).padStart(2,0)+'</b></span><br /><br />' +
          '<span style="display:inline-block;text-align:right;color:#00fc00;'+font_size3+'">HR           bpm</span><br /><br /><br /><br /><br /><br /><br /><br /><br /><br /><br /><br /><br /><br />' +
          '<span style="color:#00fcff;'+font_size2+'text-align:center;"><b>'+resp+'</b></span><br /><br />' +
          '<span style="display:inline-block;text-align:right;color:#00fcff;'+font_size3+'">RESP          bpm</span><br /><br /><br /><br /><br /><br /><br /><br /><br /><br /><br /><br />' +
          '<span style="color:#ffff00;'+font_size+'text-align:center;"><b>'+fsr_sum+'</b></span><br /><br />' +
          '<span style="display:inline-block;text-align:right;color:#ffff00;'+font_size3+'">FSR</span>',
        showarrow: false,
        font: { size: annotationFontSize, family: 'Arial', color: 'black' },
      });
    }

    setGraphData(traces.concat(peaksTraces));
    setGraphLayout({ ...baseLayout, annotations });
  };

  return (
       <div style={{ position: 'relative', width: '100%' }}>
        <div
          style={{
            position: 'absolute', left: 16, top: 10, zIndex: 2,
            color: '#fff', fontFamily: 'Arial, sans-serif', lineHeight: 1.25,
            background: 'rgba(0,0,0,0.0)', pointerEvents: 'none',
          }}
        >
          <div style={{ color: '#ffeb3b', fontSize: 18, fontWeight: 700, marginBottom: 6 }}>ECG</div>
          <div style={{ fontSize: 14, color: '#a0a0a0' }}>
            HR:&nbsp;<span style={{ color: '#00fc00', fontWeight: 700 }}>
              {String(ecgOverlay.hr || 0).padStart(2, '0')}
            </span>&nbsp;bpm
          </div>
          <div style={{ fontSize: 14, color: '#a0a0a0' }}>
            STRESS:&nbsp;<span style={{ color: '#ffffff' }}>
              {ecgOverlay.ready && ecgOverlay.stress != null ? ecgOverlay.stress.toFixed(1) : '—'}
            </span>
          </div>
          {/* <div style={{ fontSize: 14, color: '#a0a0a0' }}>
            RMSSD:&nbsp;<span style={{ color: '#ffffff' }}>
              {ecgOverlay.ready && ecgOverlay.rmssd != null ? ecgOverlay.rmssd.toFixed(1) : '—'}
            </span>&nbsp;ms
          </div>
          <div style={{ fontSize: 14, color: '#a0a0a0' }}>
            SDNN:&nbsp;<span style={{ color: '#ffffff' }}>
              {ecgOverlay.ready && ecgOverlay.sdnn != null ? ecgOverlay.sdnn.toFixed(1) : '—'}
            </span>&nbsp;ms
          </div>
          <div style={{ fontSize: 14, color: '#a0a0a0' }}>
            pNN50:&nbsp;<span style={{ color: '#ffffff' }}>
              {ecgOverlay.ready && ecgOverlay.pnn50 != null ? ecgOverlay.pnn50.toFixed(1) : '—'}
            </span>&nbsp;%
          </div> */}
        </div>

        <div
          style={{
            position: 'absolute', left: 150, top: 10, zIndex: 2,
            color: '#fff', fontFamily: 'Arial, sans-serif', lineHeight: 1.25,
            background: 'rgba(0,0,0,0.0)', pointerEvents: 'none',
          }}
        >
          <div style={{ color: '#00fc00', fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
            BCG {bcgOverlay.index !== null ? bcgOverlay.index / 2 + 1 : ''}
          </div>
          <div style={{ fontSize: 14, color: '#a0a0a0' }}>
            HR:&nbsp;<span style={{ color: '#00fc00', fontWeight: 700 }}>
              {props.hr != 0 ? props.hr : '—'}
            </span>&nbsp;bpm
          </div>
          <div style={{ fontSize: 14, color: '#a0a0a0' }}>
            STRESS:&nbsp;<span style={{ color: '#ffffff' }}>
             {bcgOverlay.ready && bcgOverlay.stress != null ? bcgOverlay.stress.toFixed(1) : '—'}
            </span>
          </div>
          {/* <div style={{ fontSize: 14, color: '#a0a0a0' }}>
            RMSSD:&nbsp;<span style={{ color: '#ffffff' }}>
              {bcgOverlay.ready && bcgOverlay.rmssd != null ? bcgOverlay.rmssd.toFixed(1) : '—'}
            </span>&nbsp;ms
          </div>
          <div style={{ fontSize: 14, color: '#a0a0a0' }}>
            SDNN:&nbsp;<span style={{ color: '#ffffff' }}>
              {bcgOverlay.ready && bcgOverlay.sdnn != null ? bcgOverlay.sdnn.toFixed(1) : '—'}
            </span>&nbsp;ms
          </div>
          <div style={{ fontSize: 14, color: '#a0a0a0' }}>
            pNN50:&nbsp;<span style={{ color: '#ffffff' }}>
              {bcgOverlay.ready && bcgOverlay.pnn50 != null ? bcgOverlay.pnn50.toFixed(1) : '—'}
            </span>&nbsp;%
          </div> */}
        </div>

        <Plot
          data={graphData}
          layout={graphLayout}
          style={{ width: '100%', height: '900px' }}
          config={{ displayModeBar: false, displaylogo: false }}
        />
    </div>
  );
};

export default HrvDash;
